import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { ContentWithInlineImages } from "@/components/common/ContentWithInlineImages";
import { CustomMarkdown } from "@/components/common/CustomMarkdown";
import { extractImageReferences } from "@/components/common/InlineImagePreview";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ExpandableTextProps {
  content: string;
  maxLength?: number;
  maxLines?: number;
}

export const ExpandableText = ({
  content,
  maxLength = 500,
  maxLines = 6,
}: ExpandableTextProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // コンテンツの改行数をカウント
  const lineBreakCount = (content.match(/\r\n|\r|\n/g) || []).length + 1;

  // コンテンツが文字数制限または行数制限を超えているかチェック
  const needsExpansion =
    content.length > maxLength || lineBreakCount > maxLines;
  const isCollapsed = needsExpansion && !isExpanded;
  const collapsedStyle: CSSProperties | undefined = isCollapsed
    ? {
        maxHeight: `${maxLines * 1.5}em`,
        WebkitMaskImage: "linear-gradient(to bottom, black 78%, transparent)",
        maskImage: "linear-gradient(to bottom, black 78%, transparent)",
      }
    : undefined;

  // コンテンツに画像参照が含まれているかチェック
  const imageReferences = useMemo(
    () => extractImageReferences(content),
    [content],
  );
  const hasImageReferences = imageReferences.length > 0;

  const handleToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="text-sm text-variable-collection-black-gray">
      <div
        className={cn(
          "max-w-full break-words",
          isCollapsed && "overflow-hidden",
        )}
        style={collapsedStyle}
      >
        {hasImageReferences ? (
          <ContentWithInlineImages
            content={content}
            references={imageReferences}
            enableEmojiShortcodeCompat
          />
        ) : (
          <CustomMarkdown content={content} enableEmojiShortcodeCompat />
        )}
      </div>

      {needsExpansion && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 h-auto p-0 text-xs text-gray-500 underline underline-offset-2"
          onClick={handleToggleClick}
        >
          {isExpanded ? t("common.showLess") : t("common.showMore")}
        </Button>
      )}
    </div>
  );
};
